/**
 * Template data processing utilities
 * Extracted from AddCanvasTemplate.js to reduce file size
 */
import { generateSnapshotFromPageDataHTML } from "../../../util/canvas/canvasHtmlSnapshot";
import { loadFromIndexedDB, openCanvasDB } from "../../../util/indexedDBUtils";
import axios from "axios";
import { BASE_URL } from "../../../config";
import { sanitizeId } from "./textDataUtils";
import { safeSetCanvasSnapshot } from "./canvasSnapshotStorage";
import { mergeMainAndOffCanvasElements } from "./templateSaveUtils";

/**
 * Prepare page data structure from canvas elements for snapshot generation
 * @param {Object} updatedElements - Canvas elements from IndexedDB
 * @returns {Object} Page data structure for snapshot generation
 */
export const preparePageDataForSnapshot = (updatedElements) => {
  return {
    shapes: [
      ...(updatedElements.rectangles || []).map((rect) => ({
        type: "rectangle",
        shape_properties: rect,
      })),
      ...(updatedElements.circles || []).map((circle) => ({
        type: "circle",
        shape_properties: circle,
      })),
      ...(updatedElements.arrows || []).map((arrow) => ({
        type: "arrow",
        shape_properties: arrow,
      })),
      ...(updatedElements.lines || []).map((line) => ({
        type: "lines",
        shape_properties: line,
      })),
      ...(updatedElements.doubleSidedArrows || []).map((arrow) => ({
        type: "double_sided_arrow",
        shape_properties: arrow,
      })),
      ...(updatedElements.texts || []).map((text) => ({
        type: "text",
        shape_properties: text,
      })),
    ],
    images: (updatedElements.images || []).map((img) => ({
      image_path: img.src || img.image_path,
      image_properties: {
        ...img,
        originalWidth: img.originalWidth,
        originalHeight: img.originalHeight,
      },
    })),
    layout_config: {
      layout: {
        page_height_vh: updatedElements?.canvasHeight || 100,
      },
    },
  };
};

/**
 * Regenerate snapshot from elements if snapshot is invalid
 * @param {Object} updatedElements - Canvas elements
 * @param {string} currentSnapshot - Current snapshot (if any)
 * @param {Array} canvasPages - Canvas pages array
 * @returns {Promise<string>} Regenerated or existing snapshot
 */
export const regenerateSnapshotIfNeeded = async (
  updatedElements,
  currentSnapshot,
  canvasPages
) => {
  // If snapshot is blank or too small, regenerate it from elements
  if (!currentSnapshot || currentSnapshot.length < 1000) {
    try {
      // Prepare page data structure for HTML snapshot generation
      const pageData = preparePageDataForSnapshot(updatedElements);

      // Generate snapshot using HTML-based approach
      const regeneratedSnapshot = await generateSnapshotFromPageDataHTML(
        pageData
      );

      // Save regenerated snapshot to localStorage
      if (regeneratedSnapshot) {
        safeSetCanvasSnapshot(regeneratedSnapshot);
        return regeneratedSnapshot;
      }
    } catch (err) {
      console.error("❌ Failed to regenerate snapshot:", err);
      // Use existing snapshot if regeneration fails
      return currentSnapshot || canvasPages[0]?.snapshot;
    }
  }
  return currentSnapshot;
};

/**
 * Check for snapshot updates from localStorage and location.state
 * @param {Object} params - Parameters object
 * @param {Array} params.canvasPages - Current canvas pages
 * @param {Object} params.location - Location object from useLocation
 * @param {Function} params.setCanvasPages - Function to update canvas pages
 * @returns {Function} Function to check for updates
 */
export const createSnapshotUpdateChecker = ({
  canvasPages,
  location,
  setCanvasPages,
}) => {
  return () => {
    const localStorageSnapshot = localStorage.getItem("canvasSnapshot");
    const locationStateSnapshot = location.state?.snapshot;
    const currentSnapshot = canvasPages[0]?.snapshot;

    // Check if we have a new snapshot from either source
    const newSnapshot = locationStateSnapshot || localStorageSnapshot;

    if (newSnapshot && newSnapshot !== currentSnapshot) {
      setCanvasPages((prevPages) => {
        const updatedPages = [...prevPages];
        if (updatedPages[0]) {
          updatedPages[0] = {
            ...updatedPages[0],
            snapshot: newSnapshot,
            hasChanges: true,
          };
        }
        return updatedPages;
      });
    }
  };
};

/**
 * Load updated canvas data from IndexedDB after returning from MyCanvas
 * @param {Object} params - Parameters object
 * @param {string} params.pageId - Page ID from localStorage
 * @param {Object} params.location - Location object
 * @param {Object} params.template - Template object
 * @param {Function} params.setInitialLoading - Function to set loading state
 * @param {Function} params.setSaveProgress - Function to update save progress
 * @param {Function} params.setTemplate - Function to update template state
 * @param {Function} params.updateCanvasPage - Function to update canvas page
 * @param {Function} params.setCanvasElements - Function to update canvas elements
 * @param {Function} params.setCanvasPages - Function to update canvas pages
 * @param {Object} params.fromMyCanvasProcessedRef - Ref to track if already processed
 * @param {Object} params.templateFetchedRef - Ref to track if template was fetched
 * @returns {Promise<void>}
 */
export const loadUpdatedDataFromIndexedDB = async ({
  pageId,
  location,
  template,
  setInitialLoading,
  setSaveProgress,
  setTemplate,
  updateCanvasPage,
  setCanvasElements,
  setCanvasPages,
  fromMyCanvasProcessedRef,
  templateFetchedRef,
}) => {
  try {
    fromMyCanvasProcessedRef.current = true; // Mark as processed immediately to prevent re-runs
    setInitialLoading(true);
    setSaveProgress({
      step: "Loading updated data...",
      percentage: 50,
    });

    // Restore template data from sessionStorage if available
    const storedTemplateData = sessionStorage.getItem("canvas_template_data");
    if (storedTemplateData) {
      try {
        const parsedTemplateData = JSON.parse(storedTemplateData);
        // If template state is empty, try to fetch it again
        // Only fetch if not already fetched for this templateId
        if (
          Object.keys(template).length === 0 &&
          parsedTemplateData.templateId &&
          templateFetchedRef.current !== parsedTemplateData.templateId
        ) {
          templateFetchedRef.current = parsedTemplateData.templateId;
          const response = await axios.get(
            `${BASE_URL}canvas-page-template/get-by-id/${parsedTemplateData.templateId}`
          );
          const data = response.data.results;
          setTemplate(data);
        }
      } catch (e) {
        console.error("Error restoring template data:", e);
        templateFetchedRef.current = false; // Reset on error
      }
    }

    let elementsResult = await loadFromIndexedDB("canvasElements", pageId);
    let offCanvasElementsResult = await loadFromIndexedDB(
      "offCanvasElements",
      pageId,
    );
    if (!elementsResult && pageId) {
      const altKey = sanitizeId(
        String(pageId).trim(),
        String(pageId || "currentCanvas"),
      );
      if (altKey && altKey !== pageId) {
        elementsResult = await loadFromIndexedDB("canvasElements", altKey);
        offCanvasElementsResult = await loadFromIndexedDB(
          "offCanvasElements",
          altKey,
        );
      }
    }

    const hasAnyElementsRecord = Boolean(elementsResult || offCanvasElementsResult);
    const updatedElements = mergeMainAndOffCanvasElements(
      elementsResult?.data || elementsResult || {},
      offCanvasElementsResult?.data || offCanvasElementsResult || {},
    );

    const el = updatedElements && typeof updatedElements === "object" ? updatedElements : {};

    if (hasAnyElementsRecord && updatedElements && typeof updatedElements === "object") {
      // Check for updated snapshot from MyCanvas (from localStorage and location.state)
      const localStorageSnapshot = localStorage.getItem("canvasSnapshot");
      const locationStateSnapshot = location.state?.snapshot;
      let updatedSnapshot = locationStateSnapshot || localStorageSnapshot;


      // Regenerate snapshot if needed
      // Get current canvasPages from setCanvasPages callback to avoid stale closure
      let currentCanvasPages;
      setCanvasPages((prevPages) => {
        currentCanvasPages = prevPages;
        return prevPages;
      });
      updatedSnapshot = await regenerateSnapshotIfNeeded(
        updatedElements,
        updatedSnapshot,
        currentCanvasPages || []
      );

      // Update the canvas page with new data and snapshot
      const pageIndex = 0; // For template, we use single page
      updateCanvasPage(pageIndex, {
        id: pageId,
        elements: {
          ...updatedElements,
          canvasHeight: updatedElements?.canvasHeight || 100,
        },
        hasChanges: true,
        snapshot: updatedSnapshot,
      });

      // Force re-render of snapshot display
      if (updatedSnapshot) {
        // Force a re-render by updating the state
        setCanvasPages((prevPages) => {
          const updatedPages = [...prevPages];
          if (updatedPages[pageIndex]) {
            updatedPages[pageIndex] = {
              ...updatedPages[pageIndex],
              snapshot: updatedSnapshot,
              hasChanges: true,
            };
          }
          return updatedPages;
        });

        safeSetCanvasSnapshot(updatedSnapshot);
      } else {
        console.error(
          "❌ No snapshot available to display - will show blank canvas"
        );
      }

      // Update canvas elements state for UI
      setCanvasElements(updatedElements);

      // Clear localStorage flags to prevent re-processing
      localStorage.removeItem("fromMyCanvas");
      localStorage.removeItem("pageId");

      setSaveProgress({
        step: "Updated data loaded successfully",
        percentage: 100,
      });

      // Clear the progress message after a short delay
      setTimeout(() => {
        setSaveProgress({ step: "", percentage: 0 });
      }, 2000);

      setInitialLoading(false);
      return; // Exit early since we've loaded the updated elements
    } else {
      setInitialLoading(false);
      fromMyCanvasProcessedRef.current = false; // Reset if no data found
    }
  } catch (error) {
    console.error(
      "[canvas-template-return] loadUpdatedDataFromIndexedDB error:",
      error,
    );
    fromMyCanvasProcessedRef.current = false; // Reset on error to allow retry
    setInitialLoading(false);
    setSaveProgress({
      step: "Error loading updated data",
      percentage: 0,
    });
    // Clear localStorage flags on error to prevent stuck state
    localStorage.removeItem("fromMyCanvas");
    localStorage.removeItem("pageId");
  }
};
